import { useCallback, useEffect, useState } from "react";
import { Download, MonitorSmartphone, Share, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { absUrl } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { applyUpdate, promptInstall, usePwa } from "@/lib/pwa";

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isAndroid = () => /android/i.test(navigator.userAgent);
const HELP_EVENT = "fmo:install-help";
const APK_EVENT = "fmo:apk-started";
const APK_URL = absUrl("/api/pwa/android.apk");

// One action per device: native prompt, else the signed FAB-MIND-OS Android app (TWA), else the single-step guide.
export async function installInOneClick() {
  let prompted = false;
  try {
    prompted = await promptInstall();
  } catch {
    prompted = false;
  }
  if (prompted) return "prompt";
  if (!isIos() && isAndroid()) {
    window.location.href = APK_URL;
    window.dispatchEvent(new Event(APK_EVENT));
    return "apk";
  }
  window.dispatchEvent(new Event(HELP_EVENT));
  return "guide";
}

const Step = ({ testId, title, children }) => (
  <li className="flex cursor-default select-text gap-3" data-testid={testId}>
    <span aria-hidden className="mt-1 h-auto w-1 shrink-0 rounded-full bg-[var(--gold)]" />
    <div className="min-w-0">
      <p className="flex flex-wrap items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--gold-bright)]">{title}</p>
      <p className="mt-1 break-words text-neutral-200">{children}</p>
    </div>
  </li>
);

const useWindowEvent = (name, handler) => {
  useEffect(() => {
    window.addEventListener(name, handler);
    return () => window.removeEventListener(name, handler);
  }, [name, handler]);
};

// Single translated line after the APK download starts.
const ApkHint = () => {
  const { t } = useI18n();
  const [shown, setShown] = useState(false);
  const show = useCallback(() => setShown(true), []);
  useWindowEvent(APK_EVENT, show);
  useEffect(() => {
    if (!shown) return undefined;
    const id = setTimeout(() => setShown(false), 30000);
    return () => clearTimeout(id);
  }, [shown]);
  if (!shown) return null;
  return (
    <div role="status" className="fixed inset-x-3 bottom-3 z-[60] mx-auto flex max-w-md items-start gap-3 rounded-2xl border border-[rgba(245,197,66,0.6)] bg-[#0d0b0c] p-3 text-sm text-neutral-100 shadow-2xl" data-testid="pwa-apk-hint">
      <Download className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold)]" />
      <p className="min-w-0 flex-1 break-words">{t("pwa.apkStarted", { browser: /SamsungBrowser/i.test(navigator.userAgent) ? "Samsung Internet" : "Chrome" })}</p>
      <button type="button" onClick={() => setShown(false)} className="shrink-0 rounded-full p-1 text-neutral-400 hover:text-white" aria-label={t("pwa.close")} data-testid="pwa-apk-hint-close">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

const deviceStep = (t) => {
  if (isIos()) return { key: "ios", title: <>iPhone / iPad (Safari) <Share className="h-4 w-4" /></>, text: t("pwa.helpIos") };
  if (isAndroid()) return { key: "android", title: "Android", text: t("pwa.helpAndroidApk") };
  return { key: "desktop", title: t("pwa.computer"), text: t("pwa.helpDesktop") };
};

// Mounted once at app level so the guide still opens when the button lives in a menu that closes on tap.
export const InstallHelpHost = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const { relatedInstalled, canPrompt } = usePwa();
  const show = useCallback(() => setOpen(true), []);
  useWindowEvent(HELP_EVENT, show);
  const step = deviceStep(t);
  const oneClick = canPrompt || (step.key === "android");
  const run = () => {
    setOpen(false);
    installInOneClick();
  };
  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="card-carbon !bg-[#0d0b0c] max-h-[90vh] w-[calc(100vw-2rem)] max-w-md overflow-y-auto rounded-2xl [&>button:last-child]:hidden" data-testid="pwa-install-help">
          <DialogClose className="absolute end-3 top-3 inline-flex h-11 w-11 items-center justify-center rounded-full border border-neutral-600 bg-black/40 text-neutral-200 transition-colors duration-200 hover:border-[var(--gold)] hover:text-white" aria-label={t("pwa.close")} data-testid="pwa-help-close-x">
            <X className="h-6 w-6" />
          </DialogClose>
          <DialogHeader className="pe-12 text-start">
            <DialogTitle className="font-display uppercase tracking-wider gold-text break-words">{t("pwa.install")}</DialogTitle>
            <DialogDescription>{t("pwa.helpIntroOne")}</DialogDescription>
          </DialogHeader>
          {relatedInstalled && <p className="rounded-xl border border-emerald-500/50 bg-emerald-500/10 p-3 text-sm text-emerald-200" data-testid="pwa-help-already">{t("pwa.alreadyInstalled")}</p>}
          <ol className="text-sm">
            <Step testId={`pwa-help-${step.key}`} title={step.title}>{step.text}</Step>
          </ol>
          <div className="flex flex-col gap-2 sm:flex-row-reverse">
            {oneClick && (
              <button type="button" onClick={run} className="btn-gold inline-flex flex-1 items-center justify-center gap-2" data-testid="pwa-help-one-click">
                <MonitorSmartphone className="h-4 w-4 shrink-0" /> {t("pwa.oneClick")}
              </button>
            )}
            <DialogClose className="inline-flex flex-1 items-center justify-center rounded-full border border-neutral-600 px-4 py-2.5 text-sm font-semibold text-neutral-100 hover:border-[var(--gold)]" data-testid="pwa-help-got-it">
              {t("pwa.gotIt")}
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
      <ApkHint />
    </>
  );
};

// "Installer en un clic": the single install button used everywhere; hidden inside the installed app (PWA or TWA).
export const InstallAppButton = ({ variant = "header", className = "" }) => {
  const { t } = useI18n();
  const { installed, standalone } = usePwa();
  if (standalone || installed) return null;
  const styles = {
    header: "hidden sm:inline-flex items-center gap-1.5 rounded-full border border-[rgba(245,197,66,0.5)] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--gold-bright)] transition-colors duration-200 hover:bg-[rgba(245,197,66,0.15)]",
    mobile: "inline-flex min-w-0 max-w-full items-center justify-center gap-1.5 text-center rounded-full border border-[rgba(245,197,66,0.6)] bg-[rgba(245,197,66,0.08)] px-3 py-1.5 text-[0.7rem] font-bold uppercase leading-tight tracking-wide text-[var(--gold-bright)]",
    menu: "inline-flex items-center justify-center gap-2 rounded-full border-2 border-[rgba(245,197,66,0.6)] px-3 py-3 text-xs font-bold uppercase tracking-wider text-[var(--gold-bright)]",
    banner: "btn-gold btn-sm",
  };
  return (
    <button type="button" onClick={installInOneClick} className={`${styles[variant]} ${className}`} aria-label={t("pwa.oneClick")} data-testid={`pwa-install-btn-${variant}`}>
      <MonitorSmartphone className="h-4 w-4 shrink-0" />
      <span className={`min-w-0 break-words ${variant === "header" ? "hidden xl:inline" : ""}`}>{t("pwa.oneClick")}</span>
    </button>
  );
};

const BANNER_KEY = "fmo_pwa_banner_dismissed_at";
const BANNER_SNOOZE_MS = 7 * 24 * 3600 * 1000;

export const InstallBanner = () => {
  const { t } = useI18n();
  const { installed, standalone } = usePwa();
  const [hidden, setHidden] = useState(() => Date.now() - Number(localStorage.getItem(BANNER_KEY) || 0) < BANNER_SNOOZE_MS);
  if (installed || standalone) return null;
  if (hidden) {
    return (
      <div className="mx-auto mt-6 flex max-w-7xl px-4 sm:px-8" data-testid="pwa-install-compact">
        <InstallAppButton variant="mobile" />
      </div>
    );
  }
  const dismiss = () => {
    localStorage.setItem(BANNER_KEY, String(Date.now()));
    setHidden(true);
  };
  return (
    <div className="mx-auto mt-6 flex max-w-7xl px-4 sm:px-8" data-testid="pwa-install-banner">
      <div className="card-carbon grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-2xl p-4 sm:grid-cols-[auto_minmax(0,1fr)_auto_auto]">
        <Download className="h-5 w-5 shrink-0 text-[var(--gold)]" />
        <p className="min-w-0 text-sm text-neutral-200">{t("pwa.banner")}</p>
        <div className="order-4 col-span-3 sm:order-3 sm:col-span-1"><InstallAppButton variant="banner" /></div>
        <button type="button" onClick={dismiss} className="order-3 rounded-full p-1.5 text-neutral-400 hover:text-white sm:order-4" aria-label={t("pwa.dismiss")} data-testid="pwa-banner-dismiss">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// "Nouvelle version disponible — Actualiser" when a new service worker is waiting.
export const UpdatePrompt = () => {
  const { t } = useI18n();
  const { updateReady } = usePwa();
  useEffect(() => {
    if (!updateReady) return;
    toast(t("pwa.update"), { id: "pwa-update", duration: Infinity, action: { label: t("pwa.refresh"), onClick: applyUpdate } });
  }, [updateReady, t]);
  return null;
};
